import { useEffect, useState, useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import {
  Keyboard,
  View,
  TouchableWithoutFeedback,
  TextInput,
  ScrollView,
  StyleSheet,
  Pressable,
} from 'react-native';
import { heightPercentageToDP as hp, widthPercentageToDP as wp } from 'react-native-responsive-screen';
import { KeyboardAccessoryNavigation } from 'react-native-keyboard-accessory';
import { useActionSheet } from '@expo/react-native-action-sheet';
import DropDownPicker from 'react-native-dropdown-picker';

import CustomText from '../components/CustomText';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Badge from '../components/Badge';
import ToggleSwitch from '../components/ToggleSwitch';
import ChipSection from '../components/ChipSection';
import AppButton from '../components/AppButton';
import { ImagePicker, UploadProgressOverlay } from '../components/report';
import BadgeEarnedModal from '../components/BadgeEarnedModal';
import BadgeProgress from '../components/BadgeProgress';
import FeatureToggleDemo from '../components/FeatureToggleDemo';

import { useResponsiveStyles, useValidChips, CHIP_TYPES, useAchievements } from '../hooks';
import useReportSubmission from '../hooks/useReportSubmission';
import Util from '../util/util.js';
import AppStyles from '../AppStyles';
import { fonts } from '../style/fonts';
import { useTranslation } from '../i18n';

const SubmitReport = (props) => {
  const { t } = useTranslation(['reports', 'common']);

  const { showActionSheetWithOptions } = useActionSheet();
  const { admin, organization, noLogin, location, userAdmin } = props.route.params;

  const { isDesktop, isWeb, screenWidth, webStyles, mobileStyles } = useResponsiveStyles();

  // Achievements data (for badge progress display)
  const { loading: achievementsLoading, nextBadge, getBadgesWithStatus, streak } = useAchievements();

  // Drawing status
  const [drawingStatus, setDrawingStatus] = useState(null);

  // Report submission hook
  const {
    description,
    setDescription,
    email,
    setEmail,
    anonymous,
    setAnonymous,
    isUrgent,
    setIsUrgent,
    KPIS,
    setKPIS,
    Locations,
    setLocations,
    imagesToSend,
    setImagesToSend,
    uploadProgress,
    uploadStage,
    setUploadStage,
    imageLoading,
    setImageLoading,
    removeImage,
    errors,
    isFormValid,
    displayErrors,
    isSubmitting,
    submitEnabled,
    setSubmitEnabled,
    validOrgs,
    OrgFilterOpen,
    setOrgFilterOpen,
    OrgFilterValue,
    setOrgFilterValue,
    OrgFilter,
    setOrgFilter,
    reportCount,
    limitedReportCount,
    executeSignIn,
    getRouting,
    updateValidOrgs,
    getUserReportCount,
    submitReport,
    earnedBadge,
    showBadgeModal,
    dismissBadgeModal,
  } = useReportSubmission(organization, noLogin);

  // Fetch organization configuration (disable autoFetch in QR mode)
  const effectiveOrg = OrgFilterValue || organization;
  const { chipValues: validKPIs, refetch: refetchKPIs } = useValidChips(effectiveOrg, CHIP_TYPES.KPI, {
    autoFetch: !noLogin,
  });
  const { chipValues: validLocations, refetch: refetchLocations } = useValidChips(
    effectiveOrg,
    CHIP_TYPES.LOCATION,
    { autoFetch: !noLogin },
  );

  // Initialize on mount
  useEffect(() => {
    const initQRMode = async () => {
      await executeSignIn();
      await getRouting();
      updateValidOrgs();
      refetchKPIs();
      refetchLocations();
    };

    if (noLogin) {
      initQRMode();
      setSubmitEnabled(true);
    } else {
      getRouting();
      updateValidOrgs();

      if (organization === 'limited') {
        getUserReportCount();
      } else {
        setSubmitEnabled(true);
      }
    }
  }, []);

  // Set location from route params
  useEffect(() => {
    if (location && validLocations.includes(location)) {
      setLocations([location]);
    }
  }, [validLocations, location, setLocations]);

  // Fetch drawing status for logged-in users (refetch on screen focus)
  useFocusEffect(
    useCallback(() => {
      const fetchDrawingStatus = async () => {
        if (noLogin || organization === 'limited') return;
        try {
          const response = await Util.get('Resty', '/items/user/drawing-status');
          if (response && !response.hasError && response.items?.length > 0) {
            const activeDrawings = response.items.map(item => item.drawing);
            const isParticipating = response.items.some(item => item.isParticipating);
            const totalEntries = response.items.reduce((sum, item) => sum + (item.entryCount || 0), 0);
            setDrawingStatus({ activeDrawings, isParticipating, totalEntries });
          } else {
            setDrawingStatus(null);
          }
        } catch (error) {
          console.log('Error fetching drawing status:', error);
        }
      };
      fetchDrawingStatus();
    }, [noLogin, organization])
  );

  const handleSelectPhoto = () => {
    if (isWeb) {
      Util.pickImage(Util.imageMode.gallery, setImageLoading, setImagesToSend);
      return;
    }
    const options = [t('submit.photoGallery'), t('submit.camera'), t('common:actions.cancel')];
    showActionSheetWithOptions({ options }, (selectedIndex) => {
      Util.pickImage(selectedIndex, setImageLoading, setImagesToSend);
    });
  };

  const handleSubmit = (tryUploadImage = true) => {
    submitReport(tryUploadImage, (reportData, routedOrg) => {
      const newPage = noLogin
        ? 'ReportSubmitted'
        : admin === true || admin === 'true'
          ? 'AdminFilterHome'
          : 'PreviousReport';

      props.navigation.navigate(newPage, {
        organization,
        admin,
        userAdmin,
        filterOrg:
          routedOrg && validOrgs.includes(routedOrg)
            ? routedOrg
            : OrgFilterValue || organization,
        noLogin,
        location,
        report: reportData,
        ...(newPage === 'AdminFilterHome' && { initialView: 'allOpen' }),
      });
    });
  };

  const showFailedActions = uploadStage === 'Failed' || Util.anyUploadsFailed(uploadProgress);

  // Responsive widths - use screenWidth for reactive updates on resize/rotation
  const cardWidth = isDesktop ? screenWidth * 0.5 : screenWidth * 0.9;

  // Display organization name (format nicely)
  const displayOrg = (OrgFilterValue || organization || '').replace(/-/g, ' ').replace(/_/g, ' ');

  return (
    <View style={[isDesktop ? webStyles.container : mobileStyles.container, styles.container]}>
      <Header organization={organization} admin={admin} noLogin={noLogin} location={location} userAdmin={userAdmin} />

      <UploadProgressOverlay
        uploadStage={uploadStage}
        uploadProgress={uploadProgress}
        isDesktop={isDesktop}
        showFailedActions={showFailedActions}
        onCancel={() => setUploadStage('Picking')}
        onRetry={() => handleSubmit(true)}
        onSubmitWithSuccessful={() => handleSubmit(false)}
      />

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <TouchableWithoutFeedback
          onPress={!isWeb ? () => Keyboard.dismiss() : undefined}
          accessible={false}
        >
          <View style={styles.contentContainer}>
            {/* Feature toggle demo banner - only shows when 'test-feature' is enabled for the org */}
            <FeatureToggleDemo width={cardWidth} />

            {/* Badge Progress Card - shows gamification status */}
            {!noLogin && organization !== 'limited' && (
              <BadgeProgress
                badges={getBadgesWithStatus().filter(b => b.earned)}
                nextBadge={nextBadge}
                nextBadgeData={getBadgesWithStatus().find(b => !b.earned)}
                streak={streak}
                loading={achievementsLoading}
                organization={displayOrg}
                width={cardWidth}
              />
            )}

            {/* Drawing status indicator */}
            {!noLogin && organization !== 'limited' && drawingStatus?.activeDrawings?.length > 0 && (
              <View style={[styles.drawingStatusContainer, { width: cardWidth }]}>
                <CustomText style={styles.drawingStatusText} size={13} color={AppStyles.colorSet.textSecondary}>
                  {(() => {
                    // Collect unique orgs from all drawings
                    const allOrgs = [...new Set(drawingStatus.activeDrawings.flatMap(d => d.organizations || []))];
                    const orgText = allOrgs.length > 0 ? ` (${allOrgs.join(', ')})` : '';

                    // Two independent plurals in one sentence: i18next allows a
                    // single `count` per key, so the inner phrase is translated
                    // first and interpolated into the outer one.
                    return drawingStatus.isParticipating
                      ? t('submit.drawing.participating', {
                          count: drawingStatus.totalEntries,
                          drawings: t('submit.drawing.drawingCount', {
                            count: drawingStatus.activeDrawings.length,
                          }),
                          orgText,
                        })
                      : t('submit.drawing.inProgress', { orgText });
                  })()}
                </CustomText>
              </View>
            )}

            {/* Organization Badge - only show for logged-in users without gamification, or QR users */}
            {(noLogin || achievementsLoading) && organization && organization !== 'limited' && (
              <View style={styles.badgeContainer}>
                <Badge label={displayOrg} icon="briefcase" />
              </View>
            )}

            {/* Limited account warning */}
            {!noLogin && organization === 'limited' && (
              <View style={[styles.warningCard, { width: cardWidth }]}>
                <CustomText
                  size={14}
                  color={reportCount >= limitedReportCount ? AppStyles.colorSet.error : '#000'}
                  font="medium"
                >
                  {t('submit.trialVersion', { used: reportCount, count: limitedReportCount })}
                </CustomText>
                <Pressable
                  onPress={() => props.navigation.navigate('Settings', { organization, admin, userAdmin })}
                >
                  <CustomText style={{ textDecorationLine: 'underline', marginTop: 4 }} size={14} color="#1a5276" font="medium">
                    {t('submit.registerToOrg')}
                  </CustomText>
                </Pressable>
              </View>
            )}

            {/* Description Card */}
            <View style={[styles.card, { width: cardWidth }]}>
              <CustomText style={styles.cardTitle} font="semiBold" size={18} color="#000">
                {t('submit.whatObserved')}
              </CustomText>
              <CustomText style={styles.cardSubtitle} font="regular" size={14} color={AppStyles.colorSet.textMuted}>
                {t('submit.subtitle')}
              </CustomText>

              <View style={styles.textAreaContainer}>
                <TextInput
                  allowFontScaling={false}
                  editable
                  multiline
                  numberOfLines={4}
                  value={description}
                  onChangeText={setDescription}
                  placeholder={t('submit.descriptionPlaceholder')}
                  placeholderTextColor={AppStyles.colorSet.textMuted}
                  style={styles.textArea}
                />
              </View>

              {displayErrors && errors.description && (
                <CustomText style={styles.errorText} size={14} color={AppStyles.colorSet.error}>
                  {errors.description}
                </CustomText>
              )}

              {/* Image picker - V3 style */}
              <ImagePicker
                images={imagesToSend}
                imageLoading={imageLoading}
                onSelectPhoto={handleSelectPhoto}
                onRemovePhoto={removeImage}
              />
            </View>

            {/* Optional email for QR mode */}
            {noLogin && (
              <View style={[styles.card, { width: cardWidth }]}>
                <CustomText style={styles.cardTitle} font="semiBold" size={18} color="#000">
                  {t('submit.emailOptional')}
                </CustomText>
                <CustomText style={{ marginBottom: 12 }} font="regular" size={14} color={AppStyles.colorSet.textSecondary}>
                  {t('submit.emailHint')}
                </CustomText>
                <View style={styles.inputContainer}>
                  <TextInput
                    allowFontScaling={false}
                    editable
                    value={email}
                    onChangeText={setEmail}
                    placeholder={t('submit.emailPlaceholder')}
                    placeholderTextColor={AppStyles.colorSet.textMuted}
                    style={styles.inputText}
                    keyboardType="email-address"
                    autoCapitalize="none"
                  />
                </View>
              </View>
            )}

            {/* Hazard Type Card */}
            <View style={[styles.card, { width: cardWidth }]}>
              <CustomText style={styles.cardTitle} font="semiBold" size={18} color="#000">
                {t('submit.hazardType')}
              </CustomText>
              <ChipSection
                variant="pill"
                doSynchronize={false}
                canAdd={true}
                canDelete={true}
                sortChips={true}
                validValues={validKPIs}
                Chips={KPIS}
                setChips={setKPIS}
              />
            </View>

            {/* Location Card */}
            <View style={[styles.card, { width: cardWidth }]}>
              <CustomText style={styles.cardTitle} font="semiBold" size={18} color="#000">
                {t('submit.location')}
              </CustomText>
              <ChipSection
                variant="pill"
                doSynchronize={false}
                canAdd={true}
                canDelete={true}
                sortChips={true}
                validValues={validLocations}
                Chips={Locations}
                setChips={setLocations}
              />
            </View>

            {/* Organization dropdown */}
            {OrgFilter != null && OrgFilter.length > 1 && (
              <View style={[styles.card, { width: cardWidth, zIndex: 1000 }]}>
                <CustomText style={styles.cardTitle} font="semiBold" size={18} color="#000">
                  {t('submit.organization')}
                </CustomText>
                <DropDownPicker
                  zIndex={1000}
                  multiple={false}
                  min={0}
                  open={OrgFilterOpen}
                  value={OrgFilterValue}
                  items={OrgFilter}
                  setOpen={setOrgFilterOpen}
                  setValue={setOrgFilterValue}
                  setItems={setOrgFilter}
                  dropDownDirection="TOP"
                  placeholder={t('submit.selectOrganization')}
                  maxHeight={158}
                  bottomOffset={200}
                  style={styles.dropdown}
                  textStyle={styles.dropdownText}
                />
              </View>
            )}

            {/* Toggle options - side by side */}
            <View style={[styles.toggleContainer, { width: cardWidth }]}>
              {/* Anonymous toggle */}
              {!noLogin && (
                <View style={styles.toggleItem}>
                  <CustomText font="medium" size={16} color="#000">
                    {t('submit.anonymous')}
                  </CustomText>
                  <ToggleSwitch value={anonymous} onValueChange={setAnonymous} />
                </View>
              )}

              {/* Urgent toggle */}
              <View style={styles.toggleItem}>
                <CustomText font="medium" size={16} color="#000">
                  {t('submit.urgent')}
                </CustomText>
                <ToggleSwitch value={isUrgent} onValueChange={setIsUrgent} />
              </View>
            </View>

            {/* Submit button */}
            <View style={[styles.submitContainer, { width: cardWidth }]}>
              <AppButton
                title={isSubmitting ? t('submit.submitting') : t('submit.submitReport')}
                onPress={() => handleSubmit(true)}
                isEnabled={submitEnabled && !isSubmitting && isFormValid}
                isBusy={isSubmitting}
                fullWidth
              />
            </View>
          </View>
        </TouchableWithoutFeedback>
      </ScrollView>

      {organization && (
        <Footer admin={admin} organization={organization} noLogin={noLogin} location={location} userAdmin={userAdmin} />
      )}
      {!isWeb && <KeyboardAccessoryNavigation androidAdjustResize avoidKeyboard />}

      {/* Badge earned celebration modal */}
      <BadgeEarnedModal
        visible={showBadgeModal}
        onClose={dismissBadgeModal}
        badge={earnedBadge}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
    backgroundColor: AppStyles.colorSet.whiteColor,
  },
  scrollContent: {
    paddingBottom: hp(4),
  },
  contentContainer: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: wp(5),
    paddingTop: hp(2),
  },
  badgeContainer: {
    alignItems: 'center',
    marginBottom: hp(1),
  },
  warningCard: {
    backgroundColor: AppStyles.colorSet.warningLight,
    borderRadius: 12,
    padding: 16,
    marginBottom: hp(2),
    alignItems: 'center',
  },
  drawingStatusContainer: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginBottom: hp(1),
    alignItems: 'center',
  },
  drawingStatusText: {
    textAlign: 'center',
  },
  card: {
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    padding: 20,
    marginBottom: hp(2),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
  },
  cardTitle: {
    marginBottom: 4,
  },
  cardSubtitle: {
    marginBottom: 12,
  },
  textAreaContainer: {
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: 12,
    minHeight: 100,
  },
  textArea: {
    fontFamily: fonts.POPPINS.regular,
    fontSize: 16,
    color: AppStyles.colorSet.textPrimary,
    padding: 16,
    minHeight: 100,
    textAlignVertical: 'top',
  },
  inputContainer: {
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  inputText: {
    fontFamily: fonts.POPPINS.regular,
    fontSize: 16,
    color: AppStyles.colorSet.textPrimary,
  },
  errorText: {
    marginTop: 8,
  },
  toggleContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 32,
    marginBottom: hp(2),
  },
  toggleItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  submitContainer: {
    marginTop: hp(1),
    marginBottom: hp(2),
  },
  dropdown: {
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderColor: AppStyles.colorSet.cardBorder,
    borderRadius: 12,
  },
  dropdownText: {
    fontFamily: fonts.POPPINS.regular,
    fontSize: 16,
    color: AppStyles.colorSet.textPrimary,
  },
});

export default SubmitReport;
